import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

/* ==============================
   STYLED COMPONENTS
============================== */

const Container = styled.div`
  width: 100%;
  margin: 15px auto;
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  font-size: 1rem;
  border: 1.5px solid #ddd;
  border-radius: 8px;
  background: #fff;
  color: #333;
  transition: all 0.3s ease;
  font-family: "Poppins", sans-serif;
  outline: none;

  &:focus {
    border-color: #ff0102;
    box-shadow: 0 0 0 2px rgba(255, 1, 2, 0.2);
  }

  &::placeholder {
    color: #aaa;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #888;
    cursor: not-allowed;
  }
`;

const Loader = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  border: 2px solid #ccc;
  border-top: 2px solid #ff0102;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: translateY(-50%) rotate(0deg);
    }
    100% {
      transform: translateY(-50%) rotate(360deg);
    }
  }
`;

/* ==============================
   CONSTANTES
============================== */

const ALLOWED_PLACE_TYPES = [
  "convenience_store",
  "store",
  "supermarket",
];

/* ==============================
   COMPONENTE PRINCIPAL
============================== */

const GooglePlacesAutocomplete = ({
  onPlaceSelected,
  styleInput = {},
  styleContainer = {},
  placeholder = "Buscar almacén...",
  ...props
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        setIsLoaded(true);
        return;
      }

      const existingScript = document.querySelector(
        'script[src*="maps.googleapis.com/maps/api/js"]'
      );

      if (existingScript) {
        existingScript.addEventListener("load", () => setIsLoaded(true));
        return;
      }

      const script = document.createElement("script");
      const key = "AIzaSyDTKWa9vWckH2AEoKsOyM0h-K0r-YFsLAg";

      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setIsLoaded(true);
      script.onerror = () => console.error("Error al cargar la Google Maps API");

      document.body.appendChild(script);
    };

    loadGoogleMapsScript();
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error("Google Maps Places API no está cargada.");
      return;
    }

    const options = {
      fields: [
        "place_id",
        "name",
        "formatted_address",
        "geometry",
        "address_components",
        "types",
      ],
    };

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    const listener = autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current.getPlace();

      if (!place.geometry) {
        console.error("No se encontró información de ubicación.");
        return;
      }

      let country = "";

      if (place.address_components) {
        place.address_components.forEach((component) => {
          if (component.types.includes("country")) {
            country = component.long_name;
          }
        });
      }

      const selectedPlace = {
        ...place,
        country,
      };

      setInputValue(place.name || "");
      onPlaceSelected(selectedPlace);
    });

    return () => {
      if (listener) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [isLoaded, onPlaceSelected]);

  return (
    <Container style={styleContainer}>
      <Input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        style={styleInput}
        disabled={!isLoaded}
        {...props}
      />

      {!isLoaded && <Loader />}
    </Container>
  );
};

export default GooglePlacesAutocomplete;